import { Button, Modal, Tooltip, Tabs, Input, message } from "antd";
import Table, { ColumnsType } from "antd/es/table";
import { usePagination } from "hooks/usePagination";
import { Link, useParams } from "react-router-dom";
import { routePath } from "config/routeMgt/routePath";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { DashboardLayout } from "components/layout/components/DashboardLayout";
import { useGetJobListingApplicants } from "features/home/hooks/useGetApplicants";
import { useGetSuggestedTalents } from "features/home/hooks/useGetSuggestedTalents";
import { useGetAllSuggestedTalents } from "features/home/hooks/useGetAllSuggestedTalents";
import {
  useGetShortListed,
  useShortlistTalent,
  useUnshortlistTalent,
} from "features/home/hooks/useGetShortListed";
import { InfoCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { useState } from "react";
import { SendMessageModal } from "features/message/components/SendMessageModal";
import saveAs from "file-saver";
import axios from "axios";
import { END_POINT } from "config/environment";

interface ApplicantType {
  id: string;
  talent_id: number;
  talent: any;
  reason: any;
  listing: any;
  score: any;
  rating: any;
  created_at: any;
}

const formatFullName = (talent: any) => {
  const getValidName = (name: string) => {
    if (!name) return "";
    return name.toLowerCase() === "null" ? "" : name;
  };

  const firstName = getValidName(talent?.first_name);
  const middleName = getValidName(talent?.middle_name);
  const lastName = getValidName(talent?.last_name);

  const nameParts = [];
  if (firstName) nameParts.push(firstName);
  if (middleName) nameParts.push(middleName);
  if (lastName) nameParts.push(lastName);

  return nameParts.filter(Boolean).join(" ");
};

export const ViewApplicants = () => {
  const params = useParams();
  const id = params.id as string;
  const { pagination, onChange } = usePagination();
  const { token } = useGetUserInfo();

  const [isReasonModalVisible, setIsReasonModalVisible] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [isSummaryModalVisible, setIsSummaryModalVisible] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState("");
  const [activeTab, setActiveTab] = useState("1");
  const [searchTerm, setSearchTerm] = useState("");
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [selectedTalentId, setSelectedTalentId] = useState<string>();
  const [isTextModalVisible, setIsTextModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState<{
    title: string;
    text: string;
  }>({
    title: "",
    text: "",
  });

  const [loadingDownload, setLoadingDownload] = useState({
    excel: false,
    pdf: false,
  });

  const {
    data,
    isLoading,
    refetch: refetchApplicants,
  } = useGetJobListingApplicants({ token, id });
  const {
    data: suggestedTalentsData,
    isLoading: isLoadingSuggested,
    refetch: refetchSuggested,
  } = useGetSuggestedTalents({ token, id });
  const {
    data: allSuggestedTalentsData,
    isLoading: isLoadingAllSuggested,
    refetch: refetchAllSuggested,
  } = useGetAllSuggestedTalents({ token, id });
  const {
    data: shortlistedData,
    isLoading: isLoadingShortlisted,
    refetch: refetchShortlisted,
  } = useGetShortListed({ id, token });
  const { mutate: shortlistTalentMutate } = useShortlistTalent();
  const { mutate: unshortlistTalentMutate, isLoading: isUnshortlisting } =
    useUnshortlistTalent();

  const shortlistedTalentIds = new Set(
    shortlistedData?.data?.map((item: any) => item.talent?.id) || [],
  );

  const handleOpenMessage = (talentId: string) => {
    setSelectedTalentId(talentId);
    setMessageModalOpen(true);
  };

  const handleOpenTextModal = (title: string, text: string) => {
    setModalContent({ title, text });
    setIsTextModalVisible(true);
  };

  const handleUnshortlistTalent = (talent: any) => {
    if (!talent?.id) return;

    unshortlistTalentMutate(
      { listingId: id, talentId: talent.id, token },
      {
        onSuccess: async () => {
          message.success("Talent removed from shortlist");
          await Promise.all([
            refetchApplicants(),
            refetchSuggested(),
            refetchAllSuggested(),
            refetchShortlisted(),
          ]);
        },
      },
    );
  };

  const handleShortlistTalent = (talent: any, source: string) => {
    shortlistTalentMutate(
      { listingId: id, talentId: talent.id, token },
      {
        onSuccess: async () => {
          message.success("Talent shortlisted successfully");

          setTimeout(async () => {
            await Promise.all([
              refetchApplicants(),
              refetchSuggested(),
              refetchAllSuggested(),
              refetchShortlisted(),
            ]);
          }, 500);
        },
        onError: (error: any) => {
          message.error(
            error.response?.data?.message || "Failed to shortlist talent",
          );
        },
      },
    );
  };

  const handleDownloadShortlistedExcel = async () => {
    setLoadingDownload((prev) => ({ ...prev, excel: true }));
    try {
      const response = await axios.post(
        `${END_POINT.BASE_URL}/v1/download-shortlisted`,
        {
          format: "excel",
          listingId: id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `shortlisted_talents_${id}.xlsx`);
      message.success("Excel file downloaded successfully");
    } catch (error) {
      message.error("Failed to download Excel file");
    } finally {
      setLoadingDownload((prev) => ({ ...prev, excel: false }));
    }
  };

  const filterTalents = (talents: any[]) => {
    if (!searchTerm) return talents;
    return talents.filter((talent) => {
      const fullName = formatFullName(talent.talent);
      const skills = Array.isArray(talent.relevantSkills)
        ? talent.relevantSkills.join(" ").toLowerCase()
        : (talent.relevantSkills || "").toString().toLowerCase();

      return (
        fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        skills.includes(searchTerm.toLowerCase())
      );
    });
  };

  const applicants = data?.data || [];
  const suggestedTalents = suggestedTalentsData?.data || [];
  const allSuggestedTalents = allSuggestedTalentsData?.data || [];
  const shortlistedTalents = shortlistedData?.data || [];

  const filteredSuggested = filterTalents(suggestedTalents);
  const allFilteredSuggested = filterTalents(allSuggestedTalents);
  const filteredShortlisted = filterTalents(shortlistedTalents);

  const handleDownloadXLSX = async () => {
    setLoadingDownload((prev) => ({ ...prev, excel: true }));
    try {
      const response = await axios.post(
        `${END_POINT.BASE_URL}/v1/download-job-applicants`,
        {
          format: "excel",
          listingId: id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `job_applicants_${id}.xlsx`);
      message.success("Excel file downloaded successfully");
    } catch (error) {
      message.error("Failed to download Excel file");
    } finally {
      setLoadingDownload((prev) => ({ ...prev, excel: false }));
    }
  };

  const formatReasonText = (reason: string): string[] | string => {
    if (!reason || reason === "No reason provided") {
      return ["No reason provided"];
    }

    try {
      const parsed = JSON.parse(reason);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch (e) {}

    const sentences = reason.split(/\.\s+/).filter((s) => s.trim().length > 0);
    if (sentences.length > 1) {
      return sentences.map((s) => s.trim() + ".");
    }

    const lines = reason.split(/\n+/).filter((s) => s.trim().length > 0);
    if (lines.length > 1) {
      return lines.map((s) => s.trim());
    }
    return reason;
  };

  const formatSummaryText = (summary: string): string[] | string => {
    if (!summary) {
      return ["No summary available"];
    }

    const sentences = summary.split(/\.\s+/).filter((s) => s.trim().length > 0);
    if (sentences.length > 1) {
      return sentences.map((s) => s.trim() + ".");
    }

    const lines = summary.split(/\n+/).filter((s) => s.trim().length > 0);
    if (lines.length > 1) {
      return lines.map((s) => s.trim());
    }
    return summary;
  };

  const getTalentSummary = (talent: any): string => {
    return talent?.executive_summary || talent?.summary || "";
  };

  const baseTalentColumns = (
    isTopTalent: boolean,
    options?: { showUnshortlist?: boolean },
  ): ColumnsType<any> => [
    {
      title: "Name",
      dataIndex: "name",
      render: (_, val) => (
        <Link
          to={routePath.talentDetails(val.talent.id).path}
          className="font-medium text-primary"
        >
          {formatFullName(val.talent)}
        </Link>
      ),
    },
    {
      title: "% Fit",
      dataIndex: "score",
      render: (_, val) => <span>{val?.score ?? "Not Rated"}</span>,
    },
    {
      title: "Relevant Skills",
      dataIndex: "relevantSkills",
      render: (relevantSkills) => {
        let parsedItems: string[] = [];

        if (Array.isArray(relevantSkills)) {
          parsedItems = relevantSkills;
        } else if (typeof relevantSkills === "string") {
          parsedItems = relevantSkills.trim().startsWith("[")
            ? JSON.parse(relevantSkills)
            : relevantSkills.split(",").map((s) => s.trim());
        }

        const capitalized = parsedItems.map((skill) =>
          skill
            .split(" ")
            .map(
              (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
            )
            .join(" "),
        );

        return capitalized.length ? (
          <span>{capitalized.join(", ")}</span>
        ) : (
          <span>No skills listed</span>
        );
      },
    },
    {
      title: "Reason",
      dataIndex: "reason",
      render: (_, val) => (
        <Tooltip title="View reason">
          <InfoCircleOutlined
            className="text-primary cursor-pointer"
            onClick={() => {
              const reasonText = val?.reason ?? "No reason provided";
              setSelectedReason(reasonText);
              setIsReasonModalVisible(true);
            }}
          />
        </Tooltip>
      ),
    },
    {
      title: "Summary",
      dataIndex: "summary",
      render: (_, val) => {
        const summary = getTalentSummary(val.talent);
        return (
          <Tooltip title="View summary">
            <InfoCircleOutlined
              className="text-primary cursor-pointer"
              onClick={() => {
                setSelectedSummary(summary || "No summary available");
                setIsSummaryModalVisible(true);
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => {
        if (options?.showUnshortlist) {
          return (
            <div className="flex gap-2">
              <Button
                danger
                loading={isUnshortlisting}
                onClick={() => handleUnshortlistTalent(record.talent)}
              >
                Unshortlist
              </Button>
              <Button
                type="primary"
                onClick={() => handleOpenMessage(record.talent.id)}
              >
                Message Talent
              </Button>
            </div>
          );
        }

        const isShortlisted = shortlistedTalentIds.has(record.talent?.id);

        return isShortlisted ? (
          <Button disabled className="bg-gray-300">
            Shortlisted
          </Button>
        ) : (
          <Button
            type="primary"
            onClick={() =>
              isTopTalent
                ? handleShortlistTalent(
                    record.talent,
                    isTopTalent ? "suggested" : "applicant",
                  )
                : handleOpenMessage(record.talent.id)
            }
          >
            {isTopTalent ? "Shortlist" : "Message Talent"}
          </Button>
        );
      },
    },
  ];

  const applicantColumns: ColumnsType<ApplicantType> = [
    {
      title: "Talent",
      dataIndex: "talent_name",
      render: (_, val) => (
        <Link
          to={routePath.talentDetails(val.talent.id).path}
          className="font-medium text-primary"
        >
          {formatFullName(val.talent)}
        </Link>
      ),
    },
    {
      title: "Job Title",
      dataIndex: "job_title",
      render: (_, val) => (
        <Link
          to={routePath.jobDetails(val.listing.slug).path}
          className="font-medium text-primary"
        >
          {val.listing.title}
        </Link>
      ),
    },
    {
      title: "% Fit",
      dataIndex: "score",
      render: (_, val) => <span>{val?.rating?.score ?? "Not Rated"}</span>,
    },
    {
      title: "Reason",
      dataIndex: "reason",
      render: (_, val) => (
        <Tooltip title="View reason">
          <InfoCircleOutlined
            className="text-primary cursor-pointer"
            onClick={() => {
              const reasonText = val?.rating?.reason ?? "No reason provided";
              setSelectedReason(reasonText);
              setIsReasonModalVisible(true);
            }}
          />
        </Tooltip>
      ),
    },
    {
      title: "Summary",
      dataIndex: "summary",
      render: (_, val) => {
        const summary =
          val?.talent?.executive_summary || val?.talent?.summary || "";
        return (
          <Tooltip title="View summary">
            <InfoCircleOutlined
              className="text-primary cursor-pointer"
              onClick={() => {
                setSelectedSummary(summary || "No summary available");
                setIsSummaryModalVisible(true);
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Application Date",
      dataIndex: "application_date",
      render: function (_, val) {
        return new Date(val?.created_at).toLocaleDateString("en-GB");
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => {
        const isShortlisted = shortlistedTalentIds.has(record.talent?.id);

        return isShortlisted ? (
          <Button disabled className="bg-gray-300">
            Shortlisted
          </Button>
        ) : (
          <Button
            type="primary"
            onClick={() => handleShortlistTalent(record.talent, "applicant")}
          >
            Shortlist
          </Button>
        );
      },
    },
  ];

  const tabs = [
    {
      key: "1",
      label: "Job Applicants",
      children: (
        <div>
          <div className="flex justify-between mb-4">
            <Button
              type="primary"
              onClick={handleDownloadXLSX}
              disabled={applicants.length === 0}
            >
              Download XLSX
            </Button>
            <Input
              placeholder="Search by name or skills"
              prefix={<SearchOutlined />}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 300 }}
              allowClear
            />
          </div>
          <Table
            className="bg-white rounded-md shadow"
            columns={applicantColumns}
            dataSource={applicants.map((item: any) => ({
              ...item,
              key: item.id,
            }))}
            loading={isLoading}
            pagination={{ ...pagination, total: data?.total }}
            onChange={onChange}
          />
        </div>
      ),
    },
    {
      key: "2",
      label: "Top Talent Matches",
      children: (
        <div>
          <div className="flex justify-end mb-4">
            <Input
              placeholder="Search by name or skills"
              prefix={<SearchOutlined />}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 300 }}
              allowClear
            />
          </div>
          <Table
            className="bg-white rounded-md shadow"
            columns={baseTalentColumns(true)}
            dataSource={filteredSuggested.map((item) => ({
              ...item,
              key: item.id,
            }))}
            loading={isLoadingSuggested}
            pagination={false}
          />
        </div>
      ),
    },
    {
      key: "3",
      label: "All Matched Talents",
      children: (
        <div>
          <div className="flex justify-end mb-4">
            <Input
              placeholder="Search by name or skills"
              prefix={<SearchOutlined />}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 300 }}
              allowClear
            />
          </div>
          <Table
            className="bg-white rounded-md shadow"
            columns={baseTalentColumns(true)}
            dataSource={allFilteredSuggested.map((item) => ({
              ...item,
              key: item.id,
            }))}
            loading={isLoadingAllSuggested}
            pagination={false}
          />
        </div>
      ),
    },
    {
      key: "4",
      label: "Talent Shortlist",
      children: (
        <div>
          <div className="flex justify-end mb-4">
            <div className="flex gap-2">
              <Button
                type="primary"
                onClick={handleDownloadShortlistedExcel}
                loading={loadingDownload.excel}
                disabled={shortlistedTalents.length === 0}
              >
                Download Excel
              </Button>
            </div>
            <Input
              placeholder="Search by name or skills"
              prefix={<SearchOutlined />}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 300 }}
              allowClear
            />
          </div>
          <Table
            className="bg-white rounded-md shadow"
            columns={baseTalentColumns(false, { showUnshortlist: true })}
            dataSource={filteredShortlisted.map((item) => ({
              ...item,
              key: item.id,
              name: formatFullName(item.talent),
              relevantSkills: item.result?.relevantSkills ?? [],
              reason: item.result?.reason,
              score: item.result?.score,
            }))}
            loading={isLoadingShortlisted}
            pagination={false}
            locale={{ emptyText: "No talents shortlisted yet" }}
          />
        </div>
      ),
    },
  ];

  return (
    <DashboardLayout container={false}>
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-6">Job Applicants</h1>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabs}
          tabBarStyle={{ marginBottom: 16 }}
        />
      </div>

      <Modal
        title="Reason for Suggestion"
        open={isReasonModalVisible}
        onCancel={() => setIsReasonModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsReasonModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={600}
        styles={{
          body: { maxHeight: "60vh", overflowY: "auto", padding: "20px" },
        }}
      >
        {(() => {
          const formattedReason = formatReasonText(selectedReason);

          if (Array.isArray(formattedReason) && formattedReason.length > 1) {
            return (
              <div className="space-y-4">
                {formattedReason.map((item, i) => (
                  <div
                    key={i}
                    className="p-4 bg-gray-50 rounded-lg border border-gray-100"
                  >
                    <p className="text-gray-700 leading-relaxed m-0">{item}</p>
                  </div>
                ))}
              </div>
            );
          }

          return (
            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
              {Array.isArray(formattedReason)
                ? formattedReason[0]
                : formattedReason}
            </p>
          );
        })()}
      </Modal>

      <Modal
        title="Talent Summary"
        open={isSummaryModalVisible}
        onCancel={() => setIsSummaryModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsSummaryModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={600}
        styles={{
          body: { maxHeight: "60vh", overflowY: "auto", padding: "20px" },
        }}
      >
        {(() => {
          const formattedSummary = formatSummaryText(selectedSummary);

          if (Array.isArray(formattedSummary) && formattedSummary.length > 1) {
            return (
              <div className="space-y-4">
                {formattedSummary.map((item, i) => (
                  <div
                    key={i}
                    className="p-4 bg-gray-50 rounded-lg border border-gray-100"
                  >
                    <p className="text-gray-700 leading-relaxed m-0">{item}</p>
                  </div>
                ))}
              </div>
            );
          }

          return (
            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
              {Array.isArray(formattedSummary)
                ? formattedSummary[0]
                : formattedSummary}
            </p>
          );
        })()}
      </Modal>

      <SendMessageModal
        open={messageModalOpen}
        onCancel={() => setMessageModalOpen(false)}
        talentId={selectedTalentId}
      />
    </DashboardLayout>
  );
};
